// src/pages/gisung_gye/GisungHistory.jsx
import React, { useState, useEffect } from "react";
import api from "../../api";

// ── 한글 금액 변환 (GisungGenerate와 동일) ──────────────────
function numberToKorean(num) {
  num = Math.floor(Number(num) || 0);
  if (num === 0) return "금 영원정";
  const units = ["", "만", "억", "조"];
  const small = ["", "일", "이", "삼", "사", "오", "육", "칠", "팔", "구"];
  const pos   = ["", "십", "백", "천"];
  let str = "", ui = 0, n = num;
  while (n > 0) {
    const chunk = n % 10000;
    if (chunk > 0) {
      let part = "";
      const d = [
        Math.floor(chunk / 1000),
        Math.floor((chunk % 1000) / 100),
        Math.floor((chunk % 100) / 10),
        chunk % 10,
      ];
      d.forEach((v, i) => { if (v !== 0) part += small[v] + pos[3 - i]; });
      str = part + units[ui] + str;
    }
    n = Math.floor(n / 10000);
    ui++;
  }
  return `금 ${str}원정`;
}

export default function GisungHistory() {
  const [list,    setList]    = useState([]);
  const [year,    setYear]    = useState(new Date().getFullYear());
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);

  // ── 수정 모달 상태 ──
  const [editRow,  setEditRow]  = useState(null);   // 수정 중인 record (null이면 닫힘)
  const [editForm, setEditForm] = useState({});
  const [saving,   setSaving]   = useState(false);
  const [editErr,  setEditErr]  = useState("");
  const [search,   setSearch]   = useState("");

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await api.get("gisung-gye/records/", { params: { year } });
      setList(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [year]);

  const onRedownload = async (r) => {
    try {
      const res = await api.get(`gisung-gye/records/${r.id}/redownload/`, {
        responseType: "blob",
      });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const a    = document.createElement("a");
      a.href     = url;
      const ym   = `${r.gisung_year}-${String(r.gisung_month).padStart(2, "0")}`;
      a.download = `${ym}_기성계_재다운.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("재다운로드 중 오류가 발생했습니다.");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("이력을 삭제하시겠습니까?\n(삭제 후 같은 월에 재생성 가능합니다)")) return;
    await api.delete(`gisung-gye/records/${id}/`);
    fetchList();
  };

  // ── 수정 열기 ──
  const openEdit = (r) => {
    setEditErr("");
    setEditRow(r);
    setEditForm({
      gisung_year:         r.gisung_year,
      gisung_month:        r.gisung_month,
      service_start:       r.service_start,
      service_end:         r.service_end,
      bill_date:           r.bill_date,
      current_amount:      r.current_amount ?? 0,
      current_amount_text: r.current_amount_text ?? "",
      prev_amount:         r.prev_amount ?? 0,
      prev_amount_text:    r.prev_amount_text ?? "",
      remaining_amount:    r.remaining_amount ?? 0,
      remaining_text:      r.remaining_text ?? "",
    });
  };

  const isGongdan = editRow?.contract_type === "공단";

  const onEditChange = (e) =>
    setEditForm(p => ({ ...p, [e.target.name]: e.target.value }));

  // 숫자 입력 → 한글 자동 변환
  const onNumChange = (numField, textField, value) => {
    const clean = String(value).replace(/,/g, "");
    setEditForm(p => ({
      ...p,
      [numField]:  clean,
      [textField]: numberToKorean(clean),
    }));
  };

  // 금회기성금액 변경 → 한글 자동변환 + 기성고잔액 실시간 재계산
  const onCurrentChange = (value) => {
    const clean = String(value).replace(/,/g, "");
    const cur   = Number(clean) || 0;
    setEditForm(p => {
      const next = { ...p, current_amount: clean, current_amount_text: numberToKorean(cur) };
      if (isGongdan && editRow) {
        const base = Number(editRow.remaining_amount) + Number(editRow.current_amount);
        const remaining = base - cur;
        next.remaining_amount = remaining;
        next.remaining_text   = numberToKorean(remaining);
      }
      return next;
    });
  };

  const onEditSave = async () => {

    setEditErr(""); setSaving(true);
    try {
      const payload = {
        gisung_year:         Number(editForm.gisung_year),
        gisung_month:        Number(editForm.gisung_month),
        service_start:       editForm.service_start,
        service_end:         editForm.service_end,
        bill_date:           editForm.bill_date,
        current_amount:      Number(editForm.current_amount) || 0,
        current_amount_text: editForm.current_amount_text,
      };
      if (isGongdan) {
        payload.prev_amount      = Number(editForm.prev_amount) || 0;
        payload.prev_amount_text = editForm.prev_amount_text;
        payload.remaining_amount = Number(editForm.remaining_amount) || 0;
        payload.remaining_text   = editForm.remaining_text;
      }

      await api.patch(`gisung-gye/records/${editRow.id}/`, payload);
      setEditRow(null);
      fetchList();
    } catch (e) {
      const d = e.response?.data;
      setEditErr(
        typeof d === "string" ? d :
        d ? Object.entries(d).map(([k, v]) => `${k}: ${v}`).join(" / ") :
        "수정 중 오류가 발생했습니다."
      );
    } finally {
      setSaving(false);
    }
  };

  const filtered = list.filter(r => {
    if (typeFilter === "그외") {
      if (r.contract_type === "구청" || r.contract_type === "공단") return false;
    } else if (typeFilter && r.contract_type !== typeFilter) {
      return false;
    }
    if (search.trim() && !r.contract_str?.toLowerCase().includes(search.trim().toLowerCase()))
      return false;
    return true;
  });


  return (
    <div style={S.page}>

      <div style={S.header}>
        <h2 style={S.title}>📁 기성 생성 이력</h2>
        <div style={S.controls}>
          <select value={year} onChange={e => setYear(e.target.value)} style={S.select}>
            {[2023, 2024, 2025, 2026].map(y => (
              <option key={y} value={y}>{y}년</option>
            ))}
          </select>
          {["", "구청", "공단", "그외"].map(t => (
            <button key={t} style={{
              ...S.filterBtn,
              ...(typeFilter === t ? S.filterActive : {})
            }} onClick={() => setTypeFilter(t)}>
              {t || "전체"}
            </button>
          ))}
          <div style={S.searchWrap}>
            <span style={S.searchIcon}>🔍</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="계약명·기관명 검색"
              style={S.searchInput}
            />
            {search && (
              <button style={S.searchClear} onClick={() => setSearch("")}>✕</button>
            )}
          </div>
        </div>
      </div>




      {/* 요약 카드 */}
      <div style={S.summaryRow}>
        {[
          { label: "전체 건수",  value: list.length + "건",              color: "#1565C0" },
          { label: "구청 등", value: list.filter(r => r.contract_type === "구청").length + "건", color: "#1565C0" },
          { label: "공단",    value: list.filter(r => r.contract_type === "공단").length + "건", color: "#2E7D32" },
          { label: "총 기성금액", value: list.reduce((s, r) => s + (r.current_amount || 0), 0).toLocaleString() + "원", color: "#E65100" },
        ].map(c => (
          <div key={c.label} style={S.summaryCard}>
            <div style={{ fontSize: 12, color: "#888" }}>{c.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* 테이블 */}
      {loading ? (
        <div style={S.empty}>불러오는 중...</div>
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {["연도/월","회차","계약 정보","용역기간","금회기성금액","청구일","생성자","수정","재다운","삭제"].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={S.empty}>이력이 없습니다.</td></tr>
              )}
              {filtered.map(r => (
                <tr key={r.id} style={S.tr}>

                  <td style={{ ...S.td, textAlign: "center", fontWeight: 700, whiteSpace: "nowrap" }}>
                    {r.gisung_year}년 {r.gisung_month}월
                  </td>


                  <td style={{ ...S.td, textAlign: "center" }}>
                    <span style={S.roundBadge}>{r.gisung_round}회차</span>
                  </td>
                  <td style={S.td}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{r.contract_str}</div>
                  </td>
                  <td style={{ ...S.td, fontSize: 12, whiteSpace: "nowrap" }}>
                    {r.service_start} ~ {r.service_end}
                  </td>
                  <td style={{ ...S.td, textAlign: "right", fontWeight: 600 }}>
                    {Number(r.current_amount).toLocaleString()}원
                  </td>
                  <td style={{ ...S.td, textAlign: "center", fontSize: 12 }}>
                    {r.bill_date}
                  </td>
                  <td style={{ ...S.td, textAlign: "center", fontSize: 12 }}>
                    {r.created_by_str || "-"}
                  </td>
                  <td style={{ ...S.td, textAlign: "center" }}>
                    <button style={S.editBtn} onClick={() => openEdit(r)}>수정</button>
                  </td>
                  <td style={{ ...S.td, textAlign: "center" }}>
                    <button style={S.redownBtn} onClick={() => onRedownload(r)}>📥</button>
                  </td>
                  <td style={{ ...S.td, textAlign: "center" }}>
                    <button style={S.delBtn} onClick={() => onDelete(r.id)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── 수정 모달 ── */}
      {editRow && (
        <div style={S.modalOverlay} onClick={() => !saving && setEditRow(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>
              ✏️ 기성 이력 수정
              <span style={S.modalSub}>{editRow.contract_str}</span>
            </div>

            <div style={S.grid2}>
              <Field label="기성 연도">
                <input name="gisung_year" type="number" value={editForm.gisung_year}
                  onChange={onEditChange} style={S.input} />
              </Field>
              <Field label="기성 월">
                <input name="gisung_month" type="number" min={1} max={12} value={editForm.gisung_month}
                  onChange={onEditChange} style={S.input} />
              </Field>
              <Field label="용역 시작일">
                <input name="service_start" type="date" value={editForm.service_start}
                  onChange={onEditChange} style={S.input} />
              </Field>
              <Field label="용역 종료일">
                <input name="service_end" type="date" value={editForm.service_end}
                  onChange={onEditChange} style={S.input} />
              </Field>
              <Field label="청구일">
                <input name="bill_date" type="date" value={editForm.bill_date}
                  onChange={onEditChange} style={S.input} />
              </Field>
            </div>

            <div style={S.modalSection}>금액</div>
            <div style={S.grid2}>
              <Field label="금회기성금액 (숫자)">
                <input type="number" value={editForm.current_amount}
                  onChange={e => onCurrentChange(e.target.value)} style={S.input} />
              </Field>
              <Field label="금회기성금액 (한글, 자동)">
                <input name="current_amount_text" value={editForm.current_amount_text} onChange={onEditChange} style={{ ...S.input, ...S.autoInput }} />
              </Field>
              {isGongdan && (
                <>
                  <Field label="기기성금액 (숫자, 고정)">
                    <input type="text"
                      value={Number(editForm.prev_amount || 0).toLocaleString()}
                      readOnly style={{ ...S.input, ...S.readOnlyInput }} />
                  </Field>
                  <Field label="기기성금액 (한글, 고정)">
                    <input value={editForm.prev_amount_text} readOnly style={{ ...S.input, ...S.readOnlyInput }} />
                  </Field>
                  <Field label="기성고잔액 (숫자, 자동)">
                    <input type="text"
                      value={Number(editForm.remaining_amount || 0).toLocaleString()}
                      readOnly style={{ ...S.input, ...S.autoInput }} />
                  </Field>
                  <Field label="기성고잔액 (한글, 자동)">
                    <input value={editForm.remaining_text} readOnly style={{ ...S.input, ...S.autoInput }} />
                  </Field>
                </>
              )}
            </div>



            {editErr && <div style={S.errorBox}>{editErr}</div>}

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button style={S.cancelBtn} onClick={() => setEditRow(null)} disabled={saving}>
                취소
              </button>
              <button style={{ ...S.saveBtn, background: saving ? "#aaa" : "#1565C0" }}
                onClick={onEditSave} disabled={saving}>
                {saving ? "저장 중..." : "💾 저장"}
              </button>
            </div>
            <p style={S.hint}>저장 시 DB에 즉시 반영되며, 기성 생성 페이지 누적 현황에도 적용됩니다.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 보조 컴포넌트 ──
function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#444" }}>{label}</label>
      {children}
    </div>
  );
}

const S = {
  page:    { padding: 24, fontFamily: '"맑은 고딕", sans-serif', maxWidth: 1600, margin: "0 auto" },

  header:  { display: "flex", flexDirection: "column", alignItems: "flex-start", marginBottom: 20, gap: 14 },
  controls: { display: "flex", flexDirection: "row", gap: 8, alignItems: "center", flexWrap: "wrap", width: "100%" },


  title:   { fontSize: 20, fontWeight: 700, margin: 0 },
  select:  { padding: "7px 10px", border: "1px solid #90a4ae", borderRadius: 6, fontSize: 13, fontWeight: 600, width: 100, fontFamily: '"맑은 고딕", sans-serif', color: "#37474f" },
  searchWrap:  { position: "relative", display: "flex", alignItems: "center" },
  searchIcon:  { position: "absolute", left: 10, fontSize: 13, pointerEvents: "none" },
  searchInput: { padding: "7px 28px 7px 30px", border: "1px solid #90a4ae", borderRadius: 6, fontSize: 13, width: 220, fontFamily: '"맑은 고딕", sans-serif', color: "#37474f", boxSizing: "border-box" },
  searchClear: { position: "absolute", right: 6, border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: "#90a4ae", padding: 4 },
  

  filterBtn:   { flex: "0 0 auto", width: "auto", padding: "7px 14px", border: "1px solid #90a4ae", borderRadius: 6, background: "#fff", color: "#37474f", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: '"맑은 고딕", sans-serif', whiteSpace: "nowrap" },

  filterActive: { background: "#1565C0", color: "#fff", borderColor: "#1565C0" },
  summaryRow:  { display: "flex", gap: 12, marginBottom: 20 },
  summaryCard: { flex: 1, background: "#fff", border: "1px solid #e0e0e0", borderRadius: 10, padding: "14px 18px" },
  tableWrap: { overflowX: "auto" },
  table:   { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:      { background: "#37474F", color: "#fff", padding: "10px 12px", textAlign: "center", fontWeight: 600, whiteSpace: "nowrap" },
  tr:      { borderBottom: "1px solid #eee" },
  td:      { padding: "10px 12px", verticalAlign: "middle" },
  empty:   { textAlign: "center", padding: 40, color: "#aaa" },
  roundBadge: { background: "#E3F2FD", color: "#1565C0", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 },
  editBtn:   { padding: "4px 12px", background: "#fff", border: "1px solid #1565C0", color: "#1565C0", borderRadius: 4, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" },
  redownBtn: { padding: "4px 10px", background: "#fff", border: "1px solid #1565C0", color: "#1565C0", borderRadius: 4, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" },
  delBtn:    { padding: "4px 12px", background: "#fff", border: "1px solid #e53935", color: "#e53935", borderRadius: 4, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" },

  // 모달
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modalBox:     { background: "#fff", borderRadius: 16, padding: "28px 32px", minWidth: 480, maxWidth: 560, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", maxHeight: "90vh", overflowY: "auto" },
  modalTitle:   { fontSize: 18, fontWeight: 700, marginBottom: 18, display: "flex", flexDirection: "column", gap: 4 },
  modalSub:     { fontSize: 13, fontWeight: 500, color: "#888" },
  modalSection: { fontSize: 14, fontWeight: 700, color: "#1565C0", margin: "18px 0 10px" },
  grid2:        { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" },
  input:        { padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13, width: "100%", boxSizing: "border-box", fontFamily: '"맑은 고딕", sans-serif' },
  autoInput:    { background: "#f0f7f0", borderColor: "#a5d6a7" },
  readOnlyInput: { background: "#f0f0f0", borderColor: "#ddd", color: "#777", cursor: "not-allowed" },
  errorBox:     { background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, padding: "10px 14px", color: "#C62828", marginTop: 14, fontSize: 13 },
  cancelBtn:    { flex: 1, padding: "12px 0", border: "1px solid #ccc", borderRadius: 8, background: "#fff", color: "#555", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  saveBtn:      { flex: 2, padding: "12px 0", border: "none", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  hint:         { fontSize: 11, color: "#aaa", textAlign: "center", margin: "10px 0 0" },
};